export const SignatureProfileHero = ({ profile }: { profile: any }) => {
  const artistFullName = [profile?.artistFirstName, profile?.artistLastName]
    .filter(Boolean)
    .join(" ");

  const reviews = (profile?.reviews as { rank?: number }[]) || [];
  const reviewCount = reviews.length;
  const averageRating =
    reviewCount > 0
      ? reviews.reduce((sum, review) => sum + (review?.rank || 0), 0) /
        reviewCount
      : 0;

  const MAX_STARS = 5;
  const fullStars = Math.floor(averageRating);
  const hasHalfStar = averageRating - fullStars >= 0.5 && fullStars < MAX_STARS;
  const emptyStars = MAX_STARS - fullStars - (hasHalfStar ? 1 : 0);

  const renderFullStar = (key: string | number) => (
    <svg
      key={`full-${key}`}
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lucide lucide-star"
    >
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>
  );

  const renderHalfStar = () => (
    <svg
      key="half"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lucide lucide-star-half"
    >
      <path d="M12 18.338a2.1 2.1 0 0 0-.987.244L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16l2.309-4.679A.53.53 0 0 1 12 2" />
    </svg>
  );

  const renderEmptyStar = (key: string | number) => (
    <svg
      key={`empty-${key}`}
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lucide lucide-star"
    >
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>
  );

  const activeSinceYear = profile?.activeSince
    ? new Date(profile.activeSince).getFullYear()
    : undefined;

  const hasPicture = Boolean(profile?.artistPictureLargeUrl);
  const hasName = Boolean(artistFullName && artistFullName.trim().length > 0);
  const hasTags =
    Array.isArray(profile?.styleTags) && profile.styleTags.length > 0;
  const hasLocation = Boolean(profile?.location);
  const hasActiveSince = Boolean(activeSinceYear);
  const showMetaRow = hasTags || hasLocation || hasActiveSince;
  const hasAbout = Boolean(profile?.about);
  const instagramLink: string | undefined = profile?.instagrams?.[0]?.visitLink;
  const facebookLink: string | undefined = profile?.facebooks?.[0]?.visitLink;
  const showSocial = Boolean(instagramLink || facebookLink);
  const followArtistLink: string | undefined = profile?.followArtistLink;
  const visitWebsiteLink: string | undefined = profile?.visitWebsiteLink;
  const contactLink: string | undefined = profile?.contactLink;

  return (
    <section className="pt-32 pb-16 px-4 md:pt-32 md:pb-24">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
          {hasPicture ? (
            <div className="lg:w-2/5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-artist-blue/20 to-artist-red/20 rounded-lg transform rotate-3" />
                {/* 2. Signature Profile artist picture (base64 encoded) */}
                <img
                  alt={artistFullName || "Artist picture"}
                  className="rounded-lg shadow-xl relative z-10 w-full max-w-md mx-auto"
                  src={profile.artistPictureLargeUrl}
                />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-artist-yellow/10 rounded-full blur-xl" />
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-artist-blue/10 rounded-full blur-xl" />
              </div>
            </div>
          ) : null}
          <div className={hasPicture ? "lg:w-3/5" : "w-full"}>
            {/* 3. Signature Profile artist name */}
            {hasName ? (
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-artist-charcoal">
                {artistFullName}
              </h1>
            ) : null}
            {/* 4. Signature Profile artist reviews. You should calculate the average rating from the reviews */}
            {reviewCount > 0 ? (
              <div className="flex items-center gap-4 mb-4">
                <div className="star-rating flex items-center">
                  {Array.from({ length: fullStars }).map((_, index) =>
                    renderFullStar(index)
                  )}
                  {hasHalfStar && renderHalfStar()}
                  {Array.from({ length: emptyStars }).map((_, index) =>
                    renderEmptyStar(index)
                  )}
                </div>
                <span className="text-muted-foreground">
                  {`${averageRating.toFixed(1)} (${reviewCount} review${
                    reviewCount === 1 ? "" : "s"
                  })`}
                </span>
              </div>
            ) : null}
            {/* 5. Signature Profile artist style tags */}
            {showMetaRow ? (
              <div className="mb-6 flex items-center text-sm text-muted-foreground">
                {hasTags
                  ? profile.styleTags.map((tag: string) => (
                      <span
                        key={tag}
                        className="mr-3 bg-artist-blue/10 text-artist-blue py-1 px-3 rounded-full"
                      >
                        {tag}
                      </span>
                    ))
                  : null}
                {/* 6. Signature Profile location */}
                {hasLocation ? (
                  <span className="mr-3">{profile.location}</span>
                ) : null}
                {/* 7. Signature Profile active since */}
                {hasActiveSince ? (
                  <span>
                    Active since&nbsp;
                    {activeSinceYear}
                  </span>
                ) : null}
              </div>
            ) : null}
            {hasAbout ? (
              <p className="text-lg mb-6 max-w-2xl">{profile.about}</p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              {followArtistLink ? (
                <a
                  href={followArtistLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 px-4 py-2 rounded-full bg-artist-accent hover:bg-artist-accent/90"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-heart mr-2"
                  >
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                  </svg>
                  Follow Artist
                </a>
              ) : null}
              {visitWebsiteLink ? (
                <a
                  href={visitWebsiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 px-4 py-2 rounded-full bg-purple-500 hover:bg-purple-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-external-link mr-2"
                  >
                    <path d="M15 3h6v6" />
                    <path d="M10 14 21 3" />
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                  Visit Website
                </a>
              ) : null}
              {contactLink ? (
                <a
                  href={contactLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 rounded-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-mail mr-2"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  Contact
                </a>
              ) : null}
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 w-10 rounded-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-share"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" x2="12" y1="2" y2="15" />
                </svg>
              </button>
            </div>
            {showSocial ? (
              <div className="flex gap-4 mt-4">
                {instagramLink ? (
                  <a
                    href={instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-artist-accent transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-instagram"
                    >
                      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                    </svg>
                    <span className="sr-only">Instagram</span>
                  </a>
                ) : null}
                {facebookLink ? (
                  <a
                    href={facebookLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-artist-accent transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-facebook"
                    >
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                    </svg>
                    <span className="sr-only">Facebook</span>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignatureProfileHero;
